import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchSportsEvents } from '../services/api';
import { Event } from '../types';
import useMetaTags from '../hooks/useMetaTags';
import { SEO_CONFIG } from '../config/seo.config';
import styles from './Home.module.css';
import '../styles/globals.css';

const Home: React.FC = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<string>('all');
    const [sportTab, setSportTab] = useState<string>('all');
    
    // SEO Meta Tags
    useMetaTags({
        title: SEO_CONFIG.pages.home.title,
        description: SEO_CONFIG.pages.home.description,
        keywords: SEO_CONFIG.pages.home.keywords,
        url: 'https://bdlivecric.com/',
        canonicalUrl: 'https://bdlivecric.com/',
        image: SEO_CONFIG.global.image,
        locale: 'bn_BD',
        robots: 'index, follow'
    });

    useEffect(() => {
        const loadEvents = async () => {
            try {
                setLoading(true);
                const data = await fetchSportsEvents();
                setEvents(data);
                setError(null);
            } catch (err) {
                const errorMsg = err instanceof Error ? err.message : 'Failed to load events';
                setError(errorMsg);
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, []);

    const getStatusBadge = (status?: string): string => {
        if (!status) return 'badge-scheduled';
        const statusMap: { [key: string]: string } = {
            live: 'badge-live',
            scheduled: 'badge-scheduled',
            completed: 'badge-completed'
        };
        return statusMap[status] || 'badge-scheduled';
    };

    const getCategoryIcon = (category?: string): string => {
        if (category === 'Cricket') return '🏏';
        if (category === 'Football') return '⚽';
        return '🎬';
    };

    const filteredEvents = filter === 'all' 
        ? events 
        : events.filter(e => e.status === filter);

    const liveEvents = events.filter(e => e.status === 'live');
    const scheduledEvents = events.filter(e => e.status === 'scheduled');
    
    const sportFilteredEvents = sportTab === 'all' 
        ? filteredEvents
        : filteredEvents.filter(e => {
            if (sportTab === 'cricket') return e.category === 'Cricket';
            if (sportTab === 'football') return e.category === 'Football';
            return true;
        });
    
    const cricketLive = events.filter(e => e.status === 'live' && e.category === 'Cricket').length;
    const footballLive = events.filter(e => e.status === 'live' && e.category === 'Football').length;
    const cricketScheduled = events.filter(e => e.status === 'scheduled' && e.category === 'Cricket').length;
    const footballScheduled = events.filter(e => e.status === 'scheduled' && e.category === 'Football').length;

    return (
        <div className={styles.home}>
            {/* Hero Section */}
            <section className={styles.hero} aria-label="লাইভ ক্রিকেট এবং ফুটবল স্ট্রিমিং হোম পেজ">
                <div className={styles.heroContent}>
                    <h1 className={styles.heroTitle}>
                        <span aria-hidden="true">🏏</span>
                        বাংলাদেশে লাইভ খেলাধুলা দেখুন বিনামূল্যে
                    </h1>
                    <p className={styles.heroSubtitle}>
                        ক্রিকেট, ফুটবল এবং আরও অনেক লাইভ ইভেন্ট স্ট্রিম করুন - কোন লুকানো খরচ নেই
                    </p>
                    <div className={styles.heroStats} role="region" aria-label="লাইভ স্পোর্টস স্টেটিস্টিক্স">
                        <div className={styles.stat}>
                            <span className={styles.statNumber} role="text">{liveEvents.length}</span>
                            <span className={styles.statLabel}>এখন লাইভ</span>
                        </div>
                        <div className={styles.stat}>
                            <span className={styles.statNumber} role="text">{scheduledEvents.length}</span>
                            <span className={styles.statLabel}>আসছে শীঘ্রই</span>
                        </div>
                        <div className={styles.stat}>
                            <span className={styles.statNumber} role="text">{events.length}</span>
                            <span className={styles.statLabel}>মোট ইভেন্ট</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Error Message */}
            {error && (
                <div className={styles.errorMessage}>
                    <span>⚠️</span> {error}
                </div>
            )}

            <div className={styles.container}>
                {/* Sport Tabs */}
                <nav className={styles.tabsSection} aria-label="খেলার ধরন ফিল্টার">
                    <div className={styles.tabs} role="tablist">
                        <button
                            onClick={() => setSportTab('all')}
                            className={`${styles.tab} ${sportTab === 'all' ? styles.active : ''}`}
                            role="tab"
                            aria-selected={sportTab === 'all'}
                            aria-controls="events-grid"
                        >
                            <span aria-hidden="true">🎬</span> সব খেলাধুলা
                        </button>
                        <button
                            onClick={() => setSportTab('cricket')}
                            className={`${styles.tab} ${sportTab === 'cricket' ? styles.active : ''}`}
                            role="tab"
                            aria-selected={sportTab === 'cricket'}
                            aria-controls="events-grid"
                        >
                            <span aria-hidden="true">🏏</span> ক্রিকেট
                            <span className={styles.badge}>{cricketLive + cricketScheduled}</span>
                        </button>
                        <button
                            onClick={() => setSportTab('football')}
                            className={`${styles.tab} ${sportTab === 'football' ? styles.active : ''}`}
                            role="tab"
                            aria-selected={sportTab === 'football'}
                            aria-controls="events-grid"
                        >
                            <span aria-hidden="true">⚽</span> ফুটবল
                            <span className={styles.badge}>{footballLive + footballScheduled}</span>
                        </button>
                    </div>
                </nav>

                {/* Filter Buttons */}
                <div className={styles.filtersSection}>
                    <div className={styles.filters} role="group" aria-label="ইভেন্ট স্ট্যাটাস ফিল্টার">
                        {['all', 'live', 'scheduled', 'completed'].map(status => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`${styles.filterBtn} ${filter === status ? styles.filterActive : ''}`}
                                aria-pressed={filter === status}
                            >
                                {status === 'all' ? '📺 সব ইভেন্ট' : status === 'live' ? '🔴 এখনই লাইভ' : status === 'scheduled' ? '📅 আসন্ন' : '✅ সম্পন্ন'}
                            </button>
                        ))}
                    </div>
                    <div className={styles.resultCount}>
                        {sportFilteredEvents.length} ইভেন্ট{sportFilteredEvents.length !== 1 ? '' : ''} পাওয়া গেছে
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className={styles.loadingState} role="status" aria-live="polite">
                        <div className={styles.spinner}>⏳</div>
                        <p>ইভেন্ট লোড হচ্ছে...</p>
                    </div>
                ) : sportFilteredEvents.length === 0 ? (
                    <div className={styles.emptyState} role="status">
                        <div className={styles.emptyIcon}>📭</div>
                        <p>কোন ইভেন্ট পাওয়া যায়নি</p>
                        <button 
                            onClick={() => { setFilter('all'); setSportTab('all'); }}
                            className={styles.resetBtn}
                        >
                            সকল ইভেন্ট দেখুন
                        </button>
                    </div>
                ) : (
                    <section className={styles.eventsGrid} id="events-grid" role="region" aria-label="লাইভ খেলাধুলার ইভেন্ট গ্রিড">
                        {sportFilteredEvents.map(event => (
                            <article key={event.id} className={styles.eventCardLink}>
                                <Link
                                    to={`/event/${event.id}`}
                                    className={styles.eventCard}
                                    aria-label={`${event.name} - ${event.date} এ ${event.location}`}
                                >
                                    {/* Header */}
                                    <header className={styles.cardHeader}>
                                        <span className={`${styles.categoryBadge} ${styles[`cat${event.category}` as keyof typeof styles]}`}>
                                            <span aria-hidden="true">{getCategoryIcon(event.category)}</span>
                                            <span>{event.category === 'Cricket' ? 'ক্রিকেট' : 'ফুটবল'}</span>
                                        </span>
                                        <div className={styles.headerRight}>
                                            {event.haveLiveIcon && (
                                                <span className={styles.liveIcon} title="লাইভ স্ট্রিমিং উপলব্ধ" aria-label="লাইভ স্ট্রিমিং উপলব্ধ">
                                                    📡
                                                </span>
                                            )}
                                            <span className={`${styles.statusBadge} ${getStatusBadge(event.status)}`}>
                                                {event.status === 'live' ? '🔴 লাইভ' : event.status === 'scheduled' ? '📅 আসন্ন' : '✅ সম্পন্ন'}
                                            </span>
                                        </div>
                                    </header>

                                    {/* Body */}
                                    <div className={styles.cardBody}>
                                        <h3 className={styles.eventName}>{event.name}</h3>
                                        
                                        <div className={styles.eventDetails}>
                                            <div className={styles.detail}>
                                                <span className={styles.icon} aria-hidden="true">📅</span>
                                                <span>{event.date}</span>
                                            </div>
                                            <div className={styles.detail}>
                                                <span className={styles.icon} aria-hidden="true">🕐</span>
                                                <span>{event.time}</span>
                                            </div>
                                            <div className={styles.detail}>
                                                <span className={styles.icon} aria-hidden="true">📍</span>
                                                <span>{event.location}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <footer className={styles.cardFooter}>
                                        <button className={styles.watchBtn} aria-label={`${event.name} দেখুন`}>
                                            <span aria-hidden="true">▶️</span> এখনই দেখুন
                                        </button>
                                    </footer>
                                </Link>
                            </article>
                        ))}
                    </section>
                )}
            </div>
        </div>
    );
};

export default Home;